/**
 * UserService - all about our user
 * - display name, avatar etc
 * - @see auth for login/logout actions etc
 * - @see organization for orgs related stuff (list of our orgs etc)
 */
import { HttpClient } from '@angular/common/http';
import type { Signal, WritableSignal } from '@angular/core';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { Observable } from 'rxjs';
import { catchError, combineLatest, firstValueFrom, map, of, switchMap, tap } from 'rxjs';

import { LOADING_STATES, LoadingState, type Maybe, replaceParams } from '@evc/web-components';

import { PlatformConfigService } from '../../../features/config/config.service';
import { CORE_API_ENDPOINTS } from '../../../features/config/config.type';
import type { ApiResponse, HttpError } from '../../../features/types/api.type';
import { PAGINATION_GET_ALL } from '../api.mock';
import { AuthService } from '../auth/service/auth.service';
import { Role } from '../types/role.type';
import type { UserProfile } from '../user/user.type';
import { CoreClientUtilService } from '../utils/core-client-utils.service';
import type {
  ApiOrganization,
  ApiOrganizationCreatePayload,
  ApiOrganizationFetchItem,
  ApiOrganizationUsers,
  ApiPaginatedOrganizationResponse,
  ApiPaginatedOrganizationUsersResponse,
  Organization,
} from './organizations.type';

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService {
  readonly #destroyRef = inject(DestroyRef);
  readonly #authService = inject(AuthService);
  readonly #httpClient = inject(HttpClient);
  readonly #configService = inject(PlatformConfigService);
  readonly #CoreClientUtilService = inject(CoreClientUtilService);

  #currentOrgId: WritableSignal<Maybe<string>> = signal(undefined);
  get currentOrgId(): Signal<Maybe<string>> {
    return this.#currentOrgId.asReadonly();
  }
  #entries: WritableSignal<Organization[]> = signal([]);
  entries = computed<Organization[]>(() => {
    const currentId = this.#currentOrgId();
    const entries = this.#entries();

    return entries.map((entry) => ({
      ...entry,
      current: entry.id === currentId,
    }));
  });
  #state = signal<LoadingState>(LOADING_STATES.PRISTINE);
  ready = computed(() => this.#state() === LOADING_STATES.READY);
  ready$ = toObservable(this.ready).pipe(takeUntilDestroyed(this.#destroyRef));

  current = computed(() => {
    if (!this.#currentOrgId()) return undefined;

    return this.entries().filter((entry) => entry.id === this.#currentOrgId())?.[0];
  });

  isCurrentUserAdminOrOwner = computed(() => {
    const currentOrg = this.current();

    return currentOrg?.userRoles?.some((role) => ['Admin', 'Owner'].includes(role.name)) ?? false;
  });

  /**
   * simply set current org ID > trigger computed so list always with {current:boolean}
   * @param followUpLogin - true only if need to retrigger auth login (new token) (eg on org click - if *connect* mode)
   */
  setCurrent(id: Maybe<string>, followUpLogin = false): Observable<void> {
    this.#currentOrgId.set(id);
    if (this.#configService.useConnect() && id && followUpLogin) {
      return this.#authService.login({
        extraQueryParameters: {
          toid: id,
        },
      });
    }

    return of(undefined);
  }

  setEntries(organizations: Organization[] | ApiOrganizationFetchItem[]): Organization[] {
    const computedOrgs: Organization[] = organizations.map((organization) =>
      this.#CoreClientUtilService.computeOrganization({
        organizationInfos: organization,
        userRoles:
          (organization as Organization).userRoles ||
          (organization as ApiOrganizationFetchItem).roles,
      }),
    );
    this.#entries.set(computedOrgs);
    const currentId = computedOrgs.find((org) => org.current)?.id;
    if (currentId) {
      this.setCurrent(currentId);
    } else {
      const newMatchId =
        this.#currentOrgId() && computedOrgs.find((org) => org.id === this.#currentOrgId())?.id;
      const newCurrentId = newMatchId || computedOrgs[0]?.id;
      this.setCurrent(newCurrentId);
    }

    if (!this.ready()) {
      this.#state.set(LOADING_STATES.READY);
    }

    return computedOrgs;
  }

  addEntry(organization: Organization): Organization {
    const computedOrg: Organization = this.#CoreClientUtilService.computeOrganization({
      organizationInfos: organization,
    });
    this.#entries.update((organizations) => [...organizations, computedOrg]);
    this.setCurrent(computedOrg.id);

    return computedOrg;
  }

  updateCurrentEntry(organization: Partial<Organization>): Organization {
    const entry = this.current();

    if (!entry) throw new Error('No current organization set');
    if (organization.id && entry.id !== organization.id)
      throw new Error('Different organization provided');

    const updatedOrganization = {
      ...entry,
      ...organization,
      current: true,
    };

    this.#entries.update((organizations) =>
      organizations.map((organizationItem) =>
        organizationItem.id === entry.id ? updatedOrganization : organizationItem,
      ),
    );

    return updatedOrganization;
  }

  /** fetch organizations list and set current one
   * * expected to call this when aquire token (cf platform/app.component) */
  async init(toid?: Maybe<string>): Promise<Organization[]> {
    // may have already been registred via <evc-platform> inputs
    if (this.#state() === LOADING_STATES.READY) {
      return this.entries();
    }

    if (this.#state() !== LOADING_STATES.PRISTINE) {
      throw new Error('Organization service already initialized');
    }

    this.#state.set(LOADING_STATES.LOADING);
    if (toid) this.setCurrent(toid);

    return this.#configService.useConnect()
      ? this.getUserOrganizations()
      : firstValueFrom(this.ready$.pipe(map(() => this.entries())));
  }

  clear(): void {
    this.setCurrent(undefined);
    this.setEntries([]);
    this.#state.set(LOADING_STATES.PRISTINE);
  }

  /** get all information (basic data + avaiable roles) for the current organiation
   * may return from memory first
   * @param force - if true, always fetch fresh data from the server
   */
  async getOrganizationInfos(force = false): Promise<Organization> {
    const cachedOrg = this.current();

    // If cached organization exists and has roles, return it directly (unless force=true)
    if (!force && cachedOrg && cachedOrg.availableRoles?.length) {
      return cachedOrg;
    }

    // Otherwise, fetch missing data
    const organization$ = this.#getOrganization$();
    const roles$ = cachedOrg?.availableRoles?.length
      ? of(cachedOrg.availableRoles)
      : this.#getOrganizationRoles$();

    const result$ = combineLatest([organization$, roles$]).pipe(
      takeUntilDestroyed(this.#destroyRef),
      map(([organization, roles]) =>
        this.#CoreClientUtilService.computeOrganization({
          organizationInfos: { ...organization, current: true },
          availableRoles: roles,
          userRoles: cachedOrg?.userRoles,
        }),
      ),
      map(this.updateCurrentEntry.bind(this)),
    );

    const result = (await firstValueFrom(result$)) as Organization;

    return result ?? cachedOrg!;
  }

  /** fetch list of all user organizations */
  async getUserOrganizations(): Promise<Organization[]> {
    return firstValueFrom(this.#getUserOrganizations$()).then((organizations) =>
      this.setEntries(organizations),
    );
  }

  /** update current organization informations (name, addresses...) */
  async updateOrganization(payload: ApiOrganization): Promise<Organization> {
    return firstValueFrom(this.#updateOrganization$(payload))
      .then((organization) =>
        this.#CoreClientUtilService.computeOrganization({ organizationInfos: organization }),
      )
      .then(this.updateCurrentEntry.bind(this));
  }

  /** check if an organization already exists with this name.
   * - throw error if not available
   * - stateless call
   */
  async checkOrganizationNameIsAvailable(name: string): Promise<boolean> {
    return firstValueFrom(this.#checkOrganizationNameIsAvailable$(name));
  }

  /** STATELESS CALL
   * TODO: CLOUD-1825 OrgService fix stateless calls : (to be unified even if we redirects after creation)
   *
   * create a new org (call api)
   * then add it to our entries
   * ! may only be used by organization app where it will redirect on creation success
   */
  async createOrganization(payload: ApiOrganizationCreatePayload): Promise<Organization> {
    return firstValueFrom(this.#createOrganization$(payload));
  }

  /** Update the organization logo */
  updateLogo$(file: File): Observable<true> {
    return this.#configService
      .safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.organization.update.logo)
      .pipe(
        switchMap((uri) => {
          const formData = new FormData();
          formData.append('Logo', file);

          return this.#httpClient.patch<ApiResponse<true>>(uri, formData);
        }),
        map(({ result }) => result),
        tap(this.#forceUpdateOrganizationInfos.bind(this)),
        takeUntilDestroyed(this.#destroyRef),
      );
  }

  /** Remove the organization logo  */
  removeLogo$(): Observable<true> {
    return this.#configService
      .safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.organization.update.logo)
      .pipe(
        switchMap((uri) => this.#httpClient.delete<ApiResponse<true>>(uri)),
        map(({ result }) => result),
        tap(this.#forceUpdateOrganizationInfos.bind(this)),
        takeUntilDestroyed(this.#destroyRef),
      );
  }

  // * ---- ORGANIZATION USERS ---- * //

  /** return the list of users for the current organization
   * and update current entry */
  async getOrganizationUsers(): Promise<UserProfile[]> {
    return firstValueFrom(this.#getOrganizationUsers$(true))
      .then((users) => this.#computeOrgUsers(users))
      .then((users) => {
        this.updateCurrentEntry({ users });

        return users;
      });
  }

  /**
   * STATELESS CALL
   * TODO CLOUD-1825 OrgService fix stateless calls : (admin:users.component do refresh users after closing modal)
   *
   * remove a user from an organization
   * expected to be called from the admin app, by an admin or owner
   * - use userId to identify the user to delete
   */
  async removeUserFromOrganization(userId: string): Promise<boolean> {
    const action = firstValueFrom(this.#removeUserFromOrganization$(userId));

    /** TODO later : store users in this service to avoid recalling api on every clicks...
    action.then((response)=>{
      // remove user from store list
      return response;
    })
    */

    return action;
  }

  /**
   * STATELESS CALL
   * TODO CLOUD-1825 OrgService fix stateless calls : (admin:users.component do update component state BUT NO service state)
   *
   * change user role in an organization
   * expected to be called from the admin app, by an admin or owner
   */
  changeUserRole$(userId: string, roleId: string): Observable<true> {
    return this.#configService
      .safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.organization.users.changeRole)
      .pipe(
        switchMap((uri) =>
          this.#httpClient.patch<ApiResponse<true>>(replaceParams(uri, { userId, roleId }), null),
        ),
        takeUntilDestroyed(this.#destroyRef),
        map(({ result }) => result),
      );
  }

  // * ---- PRIVATE METHODS ---- * //

  /** easy re-update organization data
   * > lazy solution as it would be even better to directly merge local state
   * > At least - our state keeps up to date after other updates (logo etc)
   */
  async #forceUpdateOrganizationInfos(): Promise<undefined | Organization> {
    return this.getOrganizationInfos(true).catch(() => undefined);
  }

  /** post api returns different format that the get one
   * here we ensure we have the same format */
  #computeOrgUsers(users: ApiOrganizationUsers[]): UserProfile[] {
    return users.map(({ user, roles }: ApiOrganizationUsers) =>
      this.#CoreClientUtilService.computeUserProfile({
        ...user,
        roles: roles ?? [],
      }),
    );
  }

  // * ---- API CALLS IMPLEMENTATIONS ---- * //

  #getOrganization$(): Observable<ApiOrganization> {
    return this.#configService
      .safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.organization.current)
      .pipe(
        switchMap((uri) => this.#httpClient.get<ApiResponse<ApiOrganization>>(uri)),
        takeUntilDestroyed(this.#destroyRef),
        map(({ result }) => result),
      );
  }

  #getUserOrganizations$(): Observable<ApiOrganizationFetchItem[]> {
    return this.#configService.safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.organization.list).pipe(
      switchMap((uri) => {
        const authConfig = this.#configService.get('auth');
        const params: Record<string, string | boolean> = { GetAll: true };

        if (authConfig?.forceLicense && authConfig.clientId) {
          params['applicationClientId'] = authConfig.clientId;
        }

        return this.#httpClient.get<ApiPaginatedOrganizationResponse>(uri, { params });
      }),
      takeUntilDestroyed(this.#destroyRef),
      map(({ result }) => result),
      map(({ items }) => items),
      catchError(() => of([] as ApiOrganizationFetchItem[])),
    );
  }

  #updateOrganization$(payload: ApiOrganization): Observable<ApiOrganization> {
    return this.#configService
      .safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.organization.update.information)
      .pipe(
        switchMap((uri) => this.#httpClient.post<ApiResponse<ApiOrganization>>(uri, payload)),
        map(({ result }) => result),
        takeUntilDestroyed(this.#destroyRef),
      );
  }

  #createOrganization$(payload: ApiOrganizationCreatePayload): Observable<Organization> {
    return this.#configService
      .safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.organization.create)
      .pipe(
        switchMap((uri) => this.#httpClient.post<ApiResponse<Organization>>(uri, payload)),
        map(({ result }) => result),
        takeUntilDestroyed(this.#destroyRef),
      );
  }

  #checkOrganizationNameIsAvailable$(name: string): Observable<true> {
    return this.#configService
      .safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.organization.exists)
      .pipe(
        switchMap((uri) =>
          this.#httpClient.get<ApiResponse<true>>(uri, { params: { Name: name } }),
        ),
        takeUntilDestroyed(this.#destroyRef),
        map(({ result }) => result),
        catchError((errorHttp: HttpError) => {
          const firstError = errorHttp.error?.errors?.[0];

          throw (firstError ?? errorHttp) as Error;
        }),
      );
  }

  #getOrganizationRoles$(): Observable<Role[]> {
    return this.#configService.safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.organization.roles).pipe(
      switchMap((uri) => this.#httpClient.get<ApiResponse<Role[]>>(uri)),
      takeUntilDestroyed(this.#destroyRef),
      map(({ result }) => result),
    );
  }

  getOrganizationUsersCount$(): Observable<number> {
    return this.#configService
      .safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.organization.users.count)
      .pipe(
        switchMap((uri) => this.#httpClient.get<ApiResponse<number>>(uri)),
        map(({ result }) => result),
        takeUntilDestroyed(this.#destroyRef),
        catchError(() => of(0)),
      );
  }

  #getOrganizationUsers$(getAll = true): Observable<ApiOrganizationUsers[]> {
    return this.#configService
      .safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.organization.users.list)
      .pipe(
        switchMap((uri) =>
          this.#httpClient.get<ApiPaginatedOrganizationUsersResponse>(
            uri,
            getAll ? PAGINATION_GET_ALL : undefined,
          ),
        ),
        map(({ result }) => result),
        map(({ items }) => items),
        takeUntilDestroyed(this.#destroyRef),
        catchError(() => of([])),
      );
  }

  #removeUserFromOrganization$(userId: string): Observable<boolean> {
    return this.#configService
      .safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.organization.users.delete)
      .pipe(
        switchMap((uri) =>
          this.#httpClient.delete<ApiResponse<boolean>>(replaceParams(uri, { userId })),
        ),
        map(({ result }) => result),
        takeUntilDestroyed(this.#destroyRef),
      );
  }
}
